/**
 * El nombre del comercio que va en los mensajes de una campaña por archivo («te extrañamos en <marca>») y en el
 * nombre de la campaña. Era «HATSU» fijo en el asistente: cualquier comercio que no fuera la demo les mandaba a
 * sus clientes mensajes y asuntos de mail firmados por HATSU.
 *
 * Orden: los datos de ejemplo son de HATSU (su marca); si no, el nombre legible guardado al elegir el comercio;
 * en el acceso directo a la demo, HATSU; y si no se sabe, nada: el backend usa el nombre guardado de la marca
 * (`ActivaBrandSettings.marca`) o la deja vacía, y el asistente la pide antes de armar los mensajes (antes
 * quedaba «Tu marca» y les llegaba así a los clientes). Nunca el id del comercio («hatsu-demo», un ObjectId).
 */

import { MARCA_DEMO } from "../mockData";
import { MESES, num, plural } from "./format";

export function marcaDeLaCampana(i: { esEjemplo: boolean; nombreLegible: string; brandId: string; esOperador: boolean }): string | undefined {
  if (i.esEjemplo) return MARCA_DEMO;
  const legible = (i.nombreLegible || "").trim();
  if (legible && legible !== i.brandId) return legible;
  if (!i.esOperador) return MARCA_DEMO;
  return undefined;
}

/**
 * El nombre que firma los mensajes de una campaña, o "" si no hay uno de verdad. Espejo de `marcaQueFirma` del
 * backend (`activa.service.ts`): el «Tu marca» que guardaba `analyze` cuando no sabía el nombre cuenta como vacío,
 * porque les llegaba tal cual a los clientes («te extrañamos en Tu marca») y `/cliente` saludaba «Hola, Tu marca».
 */
export function marcaQueFirma(marca: unknown): string {
  const m = typeof marca === "string" ? marca.trim() : "";
  return m && m.toLocaleLowerCase("es-AR") !== "tu marca" ? m : "";
}

/** Tope del nombre del comercio (el mismo que valida el backend, `MARCA_MAX`). */
export const MARCA_MAX = 60;

const dosDigitos = (n: number) => String(n).padStart(2, "0");

/**
 * «Reactivación del 8 de octubre, 14:32». Sin la marca adelante: la lista ya es de un comercio (está en el selector)
 * y «HATSU · Reactivación de …» se cortaba en el celular justo en la parte que distingue a una de otra.
 *
 * Era «Reactivación de octubre de 2026»: todos los análisis de un mes se llamaban igual y en la lista (y en el
 * selector de audiencia de WhatsApp oficial) no había forma de saber cuál era cuál. Con el día y la hora, dos
 * análisis del mismo día se distinguen por el nombre. Sin el año: la lista muestra la fecha completa al lado.
 */
export function nombreDeLaCampana(hoy = new Date()): string {
  return `Reactivación del ${hoy.getDate()} de ${MESES[hoy.getMonth()]}, ${dosDigitos(hoy.getHours())}:${dosDigitos(hoy.getMinutes())}`;
}

/**
 * La línea de abajo del nombre en la lista de campañas: cuándo se creó (con la hora), cuántos clientes tenía el
 * archivo y a nombre de quién salen los mensajes. Las campañas viejas se llaman todas «Reactivación de octubre de
 * 2026» y no se renombran: esto es lo que las distingue, con datos que ya trae cada una.
 */
export function detalleDeLaCampana(
  c: { createdAt?: string; marca?: string; report?: { totalClientes?: number } | null; stats?: { contactos?: number } | null },
  hoy = new Date(),
): string {
  const partes: string[] = [];
  const d = c.createdAt ? new Date(c.createdAt) : null;
  if (d && !isNaN(d.getTime())) {
    const anio = d.getFullYear() !== hoy.getFullYear() ? ` de ${d.getFullYear()}` : "";
    partes.push(`Creada el ${d.getDate()} de ${MESES[d.getMonth()]}${anio}, ${dosDigitos(d.getHours())}:${dosDigitos(d.getMinutes())}`);
  }
  const clientes = Number(c.report?.totalClientes ?? c.stats?.contactos ?? 0) || 0;
  if (clientes > 0) partes.push(`${num(clientes)} ${plural(clientes, "cliente", "clientes")}`);
  const marca = marcaQueFirma(c.marca);
  partes.push(marca ? `a nombre de ${marca}` : "sin el nombre del comercio");
  return partes.join(" · ");
}
